import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { WORKFLOW_ACTIONS, WORKFLOW_TRIGGERS } from 'modules/workflows/constants';
import {
  getIsYesNoSplitDataToggleEnabled,
  getNodes,
  getOpenedNodeData,
} from 'modules/workflows/ducks';
import { RADIO_VARIANTS } from 'ui/radio/TileRadio';
import { colors } from 'ui/theme';
import { Tooltip, TipPlacement } from 'ui/tooltip';
import { useStartNodeContext } from '../StartNodeEditor.context/StartNodeEditorContext';
import { getRadioOptions } from '../StartNodeEditor.utils';
import {
  OTHER_JOURNEY_TOOLTIP_CONTENT,
  SOURCE_SELECT_TEST_ID,
  SOURCE_WARNING_TEST_ID,
} from './SourceSelect.constants';
import {
  EntrySourceSelect,
  IconWrapper,
  RadioOptions,
  StyledTileRadio,
  StyledWarningBanner,
} from './SourceSelect.styled';

export const SourceSelect = () => {
  const initialNodeData = useSelector(getOpenedNodeData);
  const { isReadOnly, hasLinkedJourneys, nodeData, setNodeData } = useStartNodeContext();

  const radioOptions = useMemo(
    () => getRadioOptions(initialNodeData.nodeClass, nodeData.nodeClass),
    [initialNodeData.nodeClass, nodeData.nodeClass],
  );

  // Check if we should show warning banner
  const isYesNoSplitDataToggleEnabled = useSelector(getIsYesNoSplitDataToggleEnabled);
  const nodes = useSelector(getNodes);
  const eventTriggeredFieldsMatchNodes = useMemo(
    () =>
      isYesNoSplitDataToggleEnabled
        ? nodes.filter(
            ({ nodeData: currentNodeData }) =>
              currentNodeData?.nodeClass === WORKFLOW_ACTIONS.FIELDS_MATCH &&
              currentNodeData?.matchTriggeringEventFields,
          )
        : [],
    [nodes, isYesNoSplitDataToggleEnabled],
  );
  const affectedNodesLength = eventTriggeredFieldsMatchNodes.length;

  return (
    <EntrySourceSelect data-test={SOURCE_SELECT_TEST_ID}>
      {isYesNoSplitDataToggleEnabled && affectedNodesLength > 0 && (
        <StyledWarningBanner data-test={SOURCE_WARNING_TEST_ID}>
          Changing this entry source will affect{' '}
          <strong>
            {affectedNodesLength} yes/no split tile{affectedNodesLength > 1 ? 's' : ''}
          </strong>{' '}
          that filter{affectedNodesLength > 1 ? '' : 's'} on this starting event.
        </StyledWarningBanner>
      )}
      <RadioOptions>
        {radioOptions.map((opt) => {
          const Icon = opt.icon;
          const isJourneyOpt = opt.value === WORKFLOW_TRIGGERS.TRIGGERED_BY_WORKFLOW;
          const isChecked = nodeData.nodeClass === opt.value;
          // When other journeys link to the current journey,
          // we don't allow the user to change the entry source
          const isDisabled = (hasLinkedJourneys && !isJourneyOpt) || isReadOnly;
          return (
            <Tooltip
              data-test="read-only-trigger-type-tooltip"
              tooltipContent={OTHER_JOURNEY_TOOLTIP_CONTENT}
              isEnabled={hasLinkedJourneys && !isReadOnly && !isJourneyOpt}
              key={opt.label}
              placement={TipPlacement.Bottom}
            >
              <StyledTileRadio
                id={`opt-${opt.label.toLowerCase().split(' ').join('-')}`}
                isChecked={isChecked}
                value={opt.value}
                isDisabled={isDisabled}
                onClick={() => setNodeData({ nodeClass: opt.value })}
                variant={RADIO_VARIANTS.VERTICAL}
              >
                <IconWrapper>
                  <Icon
                    backgroundColor={isChecked && !isDisabled ? colors.green20 : colors.grey5}
                    lineColor={isDisabled ? colors.grey50 : colors.green100}
                    fillColor={isDisabled ? colors.grey15 : colors.green20}
                    fillSecondaryColor={isDisabled ? colors.grey25 : colors.green50}
                  />
                </IconWrapper>
                <div>{opt.label}</div>
              </StyledTileRadio>
            </Tooltip>
          );
        })}
      </RadioOptions>
    </EntrySourceSelect>
  );
};
